import clsx from 'clsx'
import compact from 'lodash/compact'
import lodashFilter from 'lodash/filter'
import map from 'lodash/map'
import noop from 'lodash/noop'
import { useQueryStates } from 'nuqs'
import React, { ComponentProps, memo, SVGProps, useCallback, useContext, useMemo } from 'react'

import SVGAngleRight from '@Assets/SVG/AngleRight'
import SVGClose from '@Assets/SVG/Close'
import { AccordionV2 } from '@Components/CMS/AccordionV2/AccordionV2'
import { DrawerPagesAsync } from '@Components/Drawer/Page'
import { FacetContent } from '@Components/Facet/Display/FacetContent'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { Wordbook } from '@Components/Wordbook'
import { PagePresentation, PageType, ProductListBlockFacetType, WordbookFragmentFragment } from '@Generated/graphql'
import { ProductSearchContext, ProductSearchStaticContext } from '@Globals/Algolia/Context'
import { createQueryStatesKeys } from '@Globals/Algolia/Shared'
import { FacetConfig } from '@Globals/Algolia/Types'
import { MetadataContext } from '@Globals/Metadata'
import { getCurrencyUnit } from '@Globals/PriceHelpers'
import { useTranslation } from '@Globals/Translations'

import styles from './CategoryFilters.module.scss'

interface FacetChip {
  // eslint-disable-next-line react/no-unused-prop-types
  id: string
  text: WordbookFragmentFragment | null

  reset(): void
}

const SVGPlus = (props: SVGProps<SVGSVGElement>) => (
  <svg height="100" stroke="black" viewBox="0 0 100 100" width="100" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M 20 50 H 80" strokeWidth="8" />
    <path d="M 50 20 V 80" strokeWidth="8" />
  </svg>
)

const CategoryFilter = ({
  facet,
  mobile,
  onFilterChange,
  renderedIndex,
}: {
  facet: FacetConfig
  mobile: boolean
  onFilterChange?: () => void
  renderedIndex: number
}) => {
  // defaultOpen if not mobile and if the renderedIndex is 1 or 2
  const defaultOpen = !mobile && (renderedIndex === 1 || renderedIndex === 2)

  return (
    <AccordionV2
      className={styles.filterAccordion}
      defaultOpen={defaultOpen}
      id={`facet-filter-${facet.key}`}
      trigger={(
        <div className={styles.filterTrigger}>
          <h3 className={styles.filterTitle}>
            <Wordbook wordbook={facet.name} />
          </h3>
          <SVGPlus />
        </div>
      )}
    >
      <div className={styles.filterContent}>
        {facet.facetDescriptionButton && facet.facetDescriptionIDs && facet.facetDescriptionIDs.length !== 0 && (
          <DrawerPagesAsync
            pages={facet.facetDescriptionIDs.map(id => ({ id, type: PageType.FacetDescription }))}
            presentation={PagePresentation.Sidebar}
            settings={{ alignment: 'left' }}
            title={<Wordbook wordbook={facet.facetDescriptionButton} />}
            triggerRender={props => (
              <button
                {...props}
                className={styles.facetDescriptionTrigger}
                type="button"
              >
                <SVGAngleRight />
                <Wordbook wordbook={facet.facetDescriptionButton} />
              </button>
            )}
          />
        )}
        <FacetContent autoApplyFilter facet={facet} onFilterChange={onFilterChange} />
      </div>
    </AccordionV2>
  )
}

const FacetChipComponent = ({ reset: onClick, text }: FacetChip) => (
  <div
    className={styles.itemsfilteredBy}
    onClick={onClick}
  >
    <p className={styles.itemFilteredText}>
      <Wordbook wordbook={text} />
    </p>
    <SVGIcon icon={SVGClose} size="9px" />
  </div>
)

const FacetChips = () => {
  const { store } = useContext(MetadataContext)
  const { t } = useTranslation()
  const { facetConfig } = useContext(ProductSearchStaticContext)
  const { facetDynamicConfig } = useContext(ProductSearchContext)
  const queryStatesKeysMap = useMemo(() => createQueryStatesKeys(facetConfig), [facetConfig])
  const [queryStates, setQueryStates] = useQueryStates(queryStatesKeysMap)

  const resetEnumerableFacet = useCallback((parameter: string, key: string) => {
    setQueryStates((prev) => {
      let nextValue = prev[parameter] as string[] | null

      if (!nextValue || !nextValue.includes(key!)) {
        return prev
      }

      nextValue = nextValue.filter(v => v !== key).sort()
      if (nextValue.length === 0) {
        nextValue = null
      }

      return {
        ...prev,
        [parameter!]: nextValue,
        page: null,
      }
    })
  }, [setQueryStates])

  const resetRangeFacet = useCallback((parameter: string) => {
    setQueryStates(() => ({
      [parameter]: null,
      page: null,
    }))
  }, [setQueryStates])

  const facetChips = useMemo(() => {
    const currencyFraction = 10 ** (store.config?.currencyFraction ?? 2)
    const currencyUnit = getCurrencyUnit(store.id)

    const options: FacetChip[] = []

    facetConfig?.forEach((facet) => {
      if (!facet || !facet.parameter) {
        return
      }

      const queryState = queryStates[facet.parameter]
      if (!queryState) {
        return
      }

      const facetDynamic = facetDynamicConfig[facet.parameter]
      if (!facetDynamic) {
        return
      }

      if (facet.type === ProductListBlockFacetType.EnumerableBoolean
        || facet.type === ProductListBlockFacetType.EnumerableColor
        || facet.type === ProductListBlockFacetType.EnumerableDefault
        || facet.type === ProductListBlockFacetType.EnumerableNumeric
      ) {
        facet.values?.forEach((value) => {
          if (!value || !value.key || !(queryState as string[]).includes(value.key)) {
            return
          }

          options.push({
            id: `${facet.parameter}-${value.key}`,
            reset: resetEnumerableFacet.bind(null, facet.parameter, value.key),
            text: value.name!,
          })
        })
        return
      }

      if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
        let defaults = '{{facetName}}: {{from}}-{{to}}'
        let [from, to] = queryState as number[]

        if (facet.type === ProductListBlockFacetType.RangePrice) {
          defaults = '{{facetName}}: {{from}}-{{to}} {{currencyUnit}}'
          from /= currencyFraction
          to /= currencyFraction
        }

        options.push({
          id: facet.parameter!,
          reset: resetRangeFacet.bind(null, facet.parameter),
          text: {
            defaults,
            values: {
              currencyUnit,
              facetName: facetDynamic.name?.defaults || '',
              from,
              to,
            },
          },
        })
        return
      }
    })

    return options
  }, [
    facetConfig,
    facetDynamicConfig,
    queryStates,
    resetEnumerableFacet,
    resetRangeFacet,
    store.config?.currencyFraction,
    store.id,
  ])

  if (!facetChips || facetChips.length === 0) {
    return null
  }

  return (
    <div className={styles.filteredBy}>
      <div className={styles.filteredByText}>{t('cms:WB_FILTERED_BY')}</div>
      {map(facetChips, props => <FacetChipComponent key={props.id} {...props} />)}
    </div>
  )
}

export const Facets = ({
  className,
  mobile = false,
  onFilterChange = noop,
}: {
  className?: string
  mobile?: boolean
  onFilterChange?: () => void
}) => {
  const { facetConfig, hideEmptyFacets } = useContext(ProductSearchStaticContext)
  const { facetDynamicConfig } = useContext(ProductSearchContext)

  // hide_empty_facets prunes here, at the top of the tree, so the item
  // components below stay on plain props: zero-count values disappear
  // unless checked (a selection stays visible and revertible), groups and
  // whole facets with nothing left to pick disappear with them
  const visibleFacetConfig = useMemo(() => {
    if (!hideEmptyFacets) {
      return facetConfig
    }

    return compact(map(facetConfig, (facet) => {
      if (facet.type !== ProductListBlockFacetType.EnumerableBoolean
        && facet.type !== ProductListBlockFacetType.EnumerableColor
        && facet.type !== ProductListBlockFacetType.EnumerableDefault
        && facet.type !== ProductListBlockFacetType.EnumerableNumeric
      ) {
        return facet
      }

      const isAvailable = (value: { id?: string | null }) => {
        const entry = facetDynamicConfig[`${facet.parameter}:${value.id}`]
        return entry?.type === 'value' && (entry.checked || entry.count > 0)
      }

      const values = lodashFilter(facet.values, isAvailable)
      const groups = compact(map(facet.groups, (group) => {
        const groupValues = lodashFilter(group.values, isAvailable)
        return groupValues.length === 0 ? null : { ...group, values: groupValues }
      }))

      if (values.length === 0 && groups.length === 0) {
        return null
      }

      return { ...facet, groups, values }
    }))
  }, [facetConfig, facetDynamicConfig, hideEmptyFacets])

  let renderedIndex = 0

  return (
    <div className={clsx(styles.filters, className)}>
      {map(visibleFacetConfig, (facet) => {
        if (!facet || !facet.key || !facet.name) {
          return null
        }

        renderedIndex += 1

        return (
          <CategoryFilter
            key={facet.key}
            facet={facet}
            mobile={mobile}
            onFilterChange={onFilterChange}
            renderedIndex={renderedIndex}
          />
        )
      })}
    </div>
  )
}

export const CategoryFacetsWithChips = memo((props: ComponentProps<typeof Facets>) => (
  <div className={styles.filter}>
    <FacetChips />
    <Facets {...props} />
  </div>
))
